/**
 * The .sysml for one subtree, regenerated from the database.
 *
 * The same claim the Files screen makes, narrowed to a node: the text shown is
 * rebuilt from stored structure rather than served from the archived original,
 * so if an edit did not reach the model it will not be here either. That is why
 * this is the first thing to reach for after a save that looked odd.
 *
 * The route is newer than this screen, so a 404 is stated as "not deployed yet"
 * rather than dressed up as a failure - it is the one error here that is about
 * the deployment rather than about the model.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { ElementRow } from '../../schema'
import { MONOSPACE } from '../../theme'

export default function ElementSourceDialog({
  row,
  onClose,
}: {
  row: ElementRow
  onClose: () => void
}) {
  const [text, setText] = useState<string | null>(null)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    api
      .exportElement(row.qualified_name)
      .then((source) => {
        if (live) setText(source)
      })
      .catch((error: ApiError) => {
        if (!live) return
        setFailure(
          error.status === 404
            ? 'Showing the source needs GET /api/elements/{qualified_name}/export, ' +
                'which is not deployed yet.'
            : error.message,
        )
      })
    return () => {
      live = false
    }
  }, [row.qualified_name])

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontFamily: MONOSPACE, fontSize: '1rem' }}>
          {row.qualified_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Regenerated from the database, not read back from {row.path || 'the imported file'}.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {failure && <Alert severity={failure.includes('not deployed') ? 'info' : 'error'}>{failure}</Alert>}
        {!text && !failure && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {text && (
          <Box
            component="pre"
            sx={{
              fontFamily: MONOSPACE,
              fontSize: '0.8rem',
              whiteSpace: 'pre',
              overflowX: 'auto',
              m: 0,
            }}
          >
            {text}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
